import React from 'react';
import { SkillsSkeletonRows, GenericSkeletonRows } from './Skeleton';

interface Column {
  key: string;
  label: string;
  render?: (value: any) => React.ReactNode;
  width?: string; 
  minWidth?: string; 
  maxWidth?: string; 
}

interface DataTableProps {
  data: Array<{
    id: string | number;
    [key: string]: any;
  }>;
  columns: Column[];
  sortConfig: {
    key: string;
    direction: 'asc' | 'desc';
  };
  handleSort: (key: string) => void;
  handleEdit: (data: any) => void;
  loading?: boolean;
  skeletonRows?: number;
  tableType?: 'skills' | 'generic';
}

const DataTable: React.FC<DataTableProps> = ({
  data,
  columns,
  sortConfig,
  handleSort,
  handleEdit,
  loading = false,
  skeletonRows = 5,
  tableType = 'generic'
}) => {
  return (
    <div className="bg-secondary-light dark:bg-secondary-dark rounded-lg shadow-sm border border-light-border-subtle dark:border-slate-700 overflow-y-auto max-h-full">
      <table className="w-full border-collapse">
        {/* Header remains visible during loading */}
        <thead className="sticky top-0 z-10 border-b border-light-border-default dark:border-slate-600">
          <tr className="bg-gray-100 shadow-sm dark:border-slate-600 dark:bg-slate-900">
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                className={`px-6 py-4 text-left text-xs font-semibold text-light-text-secondary dark:text-slate-300 uppercase tracking-wider transition-colors duration-200 select-none z-[100] ${
                  loading 
                    ? 'cursor-default' 
                    : 'cursor-pointer hover:bg-light-interactive-hover dark:hover:bg-slate-700'
                } ${column.width || ''}`}
                style={{
                  width: column.width && !column.width.startsWith('w-') ? column.width : undefined,
                  minWidth: column.minWidth,
                  maxWidth: column.maxWidth
                }}
                onClick={loading ? undefined : () => handleSort(column.key)}
              >
                <div className="flex items-center gap-2">
                  {column.label}
                  {!loading && sortConfig.key === column.key && (
                    <span className="h-4 w-4 flex items-center text-primary-light text-sm font-bold">
                      {sortConfig.direction === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        
        <tbody className="divide-y divide-light-border-subtle dark:divide-slate-700">
          {loading ? (
            // Show appropriate skeleton based on table type
            tableType === 'skills' ? (
              <SkillsSkeletonRows rowCount={skeletonRows} columns={columns} />
            ) : (
              <GenericSkeletonRows columns={columns} rowCount={skeletonRows} />
            )
          ) : (
            // Show actual data when not loading
            data.map((item, index) => (
              <tr
                key={item.id}
                className={`
                  ${index % 2 === 0 
                    ? 'bg-light-surfacePrimary dark:bg-slate-800/50' 
                    : 'bg-light-surface dark:bg-slate-800'
                  }
                  hover:bg-light-interactive-selected dark:hover:bg-slate-700 
                  transition-all duration-200 cursor-pointer 
                  group border-b border-light-border-subtle dark:border-slate-700 last:border-b-0
                `}
                onClick={() => handleEdit(item)}
              >
                {columns.map((column) => {
                  // Always use render if provided
                  if (column.render) {
                    // For 'action' column, pass the full row data
                    if (column.key === 'action') {
                      return (
                        <td
                          key={column.key}
                          className="px-6 py-4"
                        >
                          {column.render(item)}
                        </td>
                      );
                    }
                    // For other columns, pass just the value
                    return (
                      <td
                        key={column.key}
                        className={`px-6 py-4 text-sm font-medium text-light-text-primary dark:text-slate-200 ${column.width || ''}`}
                        style={{
                          width: column.width && !column.width.startsWith('w-') ? column.width : undefined,
                          minWidth: column.minWidth,
                          maxWidth: column.maxWidth
                        }}
                      >
                        <div className="flex items-center">
                          {column.render(item[column.key])}
                        </div>
                      </td>
                    );
                  }
                  // Default rendering if no render function
                  return (
                    <td
                      key={column.key}
                      className={`px-6 py-4 text-sm font-medium text-light-text-primary dark:text-slate-200 ${column.width || ''}`}
                      style={{
                        width: column.width && !column.width.startsWith('w-') ? column.width : undefined,
                        minWidth: column.minWidth,
                        maxWidth: column.maxWidth
                      }}
                    >
                      <div className="flex items-center">
                        {item[column.key]}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))
          )}
        </tbody>
      </table>

      {/* Empty state - only show when not loading and no data */}
      {!loading && data.length === 0 && (
        <div className="text-center py-12">
          <div className="text-light-text-tertiary dark:text-slate-400 text-sm">
            No data available
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;